/**
 * Firm Settings › Data and security › Documents & retention: how long documents are kept, what is past that period now,
 * and the two-step removal (clients get notice to download, then the owner deletes).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { ask } from "@/components/dialogs";
import { Empty, Loading, Section } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { formatBytes, formatDate, localToday, plural } from "@/lib/format";
import { TableScroll } from "@/components/table-scroll";

type Policy = { years: number; payrollYears: number };
type Run = { id: string; status: "notified" | "deleting" | "deleted" | "cancelled"; files: number; deletedCount: number; bytes: number; clients: number; unreachable: string[]; deleteAfter: string; createdAt: string; decidedAt: string | null };
type Retention = {
  policy: Policy;
  minimum: Policy;
  today: string;
  cutoffs: { tax: number; other: number; payroll: number };
  due: Array<{ year: number | null; group: string; files: number; bytes: number }>;
  totals: { files: number; bytes: number; clients: number };
  runs: Run[];
  canDelete: boolean;
};

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const KEY = ["/api/firm/retention"];

export function RetentionPanel() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<Retention>({ queryKey: KEY });
  const [policy, setPolicy] = useState<Policy | null>(null);
  useEffect(() => setPolicy(data?.policy ?? null), [data?.policy]);
  const done = () => qc.invalidateQueries({ queryKey: KEY });
  const save = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("PUT", "/api/firm/retention/policy", policy), onSuccess: done });
  const notify = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", "/api/firm/retention/notify"), onSuccess: done });
  const act = useMutation({ meta: { inlineError: true }, mutationFn: (v: { id: string; what: "delete" | "cancel" }) => api.send("POST", `/api/firm/retention/${v.id}/${v.what}`), onSuccess: done });

  // The section and its heading show while the policy loads (and until the form takes it), holding
  // about the height of the form.
  if (isLoading || (data && !policy))
    return (
      <Section title="Record retention" description="Documents are kept for the period below, then removed only after clients have had notice to download them and the owner approves. Nothing is deleted automatically.">
        <Loading size="list" />
      </Section>
    );
  if (error || !data || !policy) return <Empty>{error ? errorMessage(error) : "Not available."}</Empty>;
  const open = data.runs.find((r) => r.status === "notified" || r.status === "deleting");
  const err = save.error ?? notify.error ?? act.error;
  const changed = policy.years !== data.policy.years || policy.payrollYears !== data.policy.payrollYears;
  const ready = open && localToday() >= open.deleteAfter;

  return (
    <Section title="Record retention" description="Documents are kept for the period below, then removed only after clients have had notice to download them and the owner approves. Nothing is deleted automatically.">
      <div className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="space-y-1">
            <Label className="block" htmlFor="ret-years">Keep documents</Label>
            <select id="ret-years" className={select} value={policy.years} onChange={(e) => setPolicy({ ...policy, years: Number(e.target.value) })}>
              {[3, 4, 5, 6, 7, 10].map((n) => (
                <option key={n} value={n}>
                  {n} years after filing{n === data.minimum.years ? " (minimum)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="block" htmlFor="ret-payroll">Keep payroll records</Label>
            <select id="ret-payroll" className={select} value={policy.payrollYears} onChange={(e) => setPolicy({ ...policy, payrollYears: Number(e.target.value) })}>
              {[4, 5, 6, 7, 10].map((n) => (
                <option key={n} value={n}>
                  {n} years{n === data.minimum.payrollYears ? " (minimum)" : ""}
                </option>
              ))}
            </select>
          </div>
          {changed && (
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              Save
            </Button>
          )}
        </div>
        <ul className="space-y-1 text-xs text-muted-foreground">
          <li>Tax folders are named for the year the return is filed: the {data.cutoffs.tax} folder and earlier may be removed now.</li>
          <li>Other folders cover the year of the records, filed the following year: {data.cutoffs.other} and earlier may be removed now ({data.cutoffs.payroll} and earlier for payroll).</li>
          <li>Permanent folders (IDs, formation papers, asset records) are kept until the client has been archived for the same period. Documents of work still open are never included.</li>
        </ul>

        {open ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-700/50 dark:bg-amber-950/30">
            <p className="font-medium">
              {open.files.toLocaleString()} file{open.files === 1 ? "" : "s"} ({formatBytes(open.bytes)}, {open.clients} client{open.clients === 1 ? "" : "s"}) {ready ? "can now be deleted" : `can be deleted from ${formatDate(open.deleteAfter)}`}.
            </p>
            <p className="mt-1 text-muted-foreground">
              Clients were emailed on {formatDate(open.createdAt)} with the years affected and how to download them. Each file is checked again when you delete: anything that has since become needed (work reopened, a client back) is kept.
            </p>
            {open.unreachable.length > 0 && (
              <p className="mt-2">
                <span className="font-medium">Not emailed</span> (archived or no portal access), so send them their files another way first: {open.unreachable.join(", ")}.
              </p>
            )}
            {open.status === "deleting" && <p className="mt-2 font-medium">Deleting stopped part way ({open.deletedCount} removed). Click Delete permanently again to finish.</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {data.canDelete && (
                <Button
                  variant="destructive"
                  disabled={!ready || act.isPending}
                  onClick={async () => {
                    if (await ask({ title: "Delete these documents permanently?", description: `${plural(open.files, "file")} will be removed from storage. Each file's name and dates stay in the audit trail. This can't be undone.`, confirmLabel: "Delete permanently", destructive: true }))
                      act.mutate({ id: open.id, what: "delete" });
                  }}
                >
                  Delete permanently
                </Button>
              )}
              <Button variant="outline" disabled={act.isPending} onClick={() => act.mutate({ id: open.id, what: "cancel" })}>
                Cancel this deletion
              </Button>
            </div>
          </div>
        ) : data.totals.files === 0 ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-success" aria-hidden /> No documents are past the retention period.
          </p>
        ) : (
          <div className="space-y-3">
            <TableScroll label="Documents past retention, by folder year table">
              <table className="w-full min-w-[420px] text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="py-2 pr-4 font-medium">Folder year</th>
                    <th className="py-2 pr-4 font-medium">Service</th>
                    <th className="py-2 pr-4 text-right font-medium">Files</th>
                    <th className="py-2 text-right font-medium">Size</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.due.map((r) => (
                    <tr key={`${r.year}-${r.group}`}>
                      <td className="py-2 pr-4">{r.year ?? "Permanent (archived clients)"}</td>
                      <td className="py-2 pr-4">{r.group}</td>
                      <td className="tabular py-2 pr-4 text-right">{r.files.toLocaleString()}</td>
                      <td className="tabular py-2 text-right">{formatBytes(r.bytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            {data.canDelete ? (
              <Button
                variant="outline"
                disabled={notify.isPending}
                onClick={async () => {
                  if (await ask({ title: "Give clients notice?", description: `Clients with documents in these years are emailed that they'll be removed in 60 days, with how to download them. You delete them yourself after that.`, confirmLabel: "Email clients" }))
                    notify.mutate();
                }}
              >
                Give clients 60 days' notice
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">Only the firm's owner can start a deletion.</p>
            )}
          </div>
        )}
        {err && <p className="text-sm text-destructive">{errorMessage(err)}</p>}
        {data.runs.some((r) => r.status === "deleted" || r.status === "cancelled") && (
          <div>
            <p className="mb-1 text-xs font-semibold text-muted-foreground">History</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {data.runs
                .filter((r) => r.status === "deleted" || r.status === "cancelled")
                .map((r) => (
                  <li key={r.id}>
                    {formatDate(r.decidedAt ?? r.createdAt)}: {r.status === "cancelled"
                      ? `deletion of ${plural(r.files, "file")} cancelled`
                      : r.deletedCount === r.files
                        ? `${plural(r.files, "file")} deleted`
                        : `${r.deletedCount.toLocaleString()} of ${plural(r.files, "file")} deleted (the rest were needed again and kept)`}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>
    </Section>
  );
}
